/**
 * Timeline Ruler Component
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePlaybackStore } from "../store/playbackStore";
import { useSessionStore } from "../store/sessionStore";
import "./TimelineRuler.css";

const getTickSteps = (zoom: number) => {
  if (zoom >= 220) return { major: 1, minor: 0.25 };
  if (zoom >= 140) return { major: 1, minor: 0.5 };
  if (zoom >= 80) return { major: 2, minor: 0.5 };
  if (zoom >= 45) return { major: 5, minor: 1 };
  return { major: 10, minor: 2 };
};

const formatTimeLabel = (seconds: number, majorStep: number): string => {
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;

  if (majorStep < 1) {
    return `${min}:${sec.toFixed(1).padStart(4, "0")}`;
  }

  return `${min}:${Math.floor(sec).toString().padStart(2, "0")}`;
};

const TimelineRuler: React.FC = () => {
  const { zoom, playheadMs, setPlayheadMs, horizontalScrollPx, setHorizontalScroll } = usePlaybackStore();
  const { tracks, drafts } = useSessionStore();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState(0);

  const totalDurationMs = useMemo(() => {
    const trackEnds = tracks.map((track) => (track.offsetMs || 0) + (track.durationMs || 0));
    const draftEnds = drafts.map((draft) => (draft.offsetMs || 0) + (draft.durationMs || 0));
    return Math.max(1000, ...trackEnds, ...draftEnds);
  }, [tracks, drafts]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    const updateWidth = () => {
      setViewportWidth(node.clientWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    if (Math.abs(node.scrollLeft - horizontalScrollPx) > 1) {
      node.scrollLeft = horizontalScrollPx;
    }
  }, [horizontalScrollPx]);

  const contentWidthPx = Math.max(viewportWidth, Math.ceil((totalDurationMs / 1000) * zoom));
  const tickSteps = getTickSteps(zoom);

  const ticks = useMemo(() => {
    const list: { position: number; major: boolean; label?: string }[] = [];
    const maxSeconds = contentWidthPx / zoom;

    for (let sec = 0; sec <= maxSeconds + tickSteps.minor; sec += tickSteps.minor) {
      const normalized = sec / tickSteps.major;
      const isMajor = Math.abs(normalized - Math.round(normalized)) < 0.0001;

      list.push({
        position: sec * zoom,
        major: isMajor,
        label: isMajor ? formatTimeLabel(sec, tickSteps.major) : undefined,
      });
    }

    return list;
  }, [contentWidthPx, zoom, tickSteps.major, tickSteps.minor]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const node = scrollRef.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    const localX = event.clientX - rect.left + node.scrollLeft;
    const nextMs = Math.max(0, Math.round((localX / zoom) * 1000));
    setPlayheadMs(nextMs);
  };

  const playheadPosition = (playheadMs / 1000) * zoom;

  return (
    <div className="timeline-ruler">
      <div className="timeline-gutter">
        <span className="timeline-gutter-label">Timeline</span>
      </div>

      <div
        ref={scrollRef}
        className="timeline-scroll"
        onScroll={(e) => setHorizontalScroll((e.currentTarget as HTMLDivElement).scrollLeft)}
        onClick={handleClick}
      >
        <div className="ruler-content" style={{ width: `${contentWidthPx}px` }}>
          {ticks.map((tick, i) => (
            <div
              key={`${tick.position}-${i}`}
              className={`ruler-tick ${tick.major ? "major" : "minor"}`}
              style={{ left: `${tick.position}px` }}
            >
              <div className="tick-mark" />
              {tick.label ? <div className="tick-label">{tick.label}</div> : null}
            </div>
          ))}

          <div className="playhead" style={{ left: `${playheadPosition}px` }} />
        </div>
      </div>
    </div>
  );
};

export default TimelineRuler;
